/**
 * Calls for the public Self-Serve class landing (`/api/public/ss-classes/{slug}`).
 * Uses plain axios on purpose: these pages are anonymous, so no admin token or admin
 * interceptors (e.g. redirect to login on 401) may run. The only credential sent is the
 * Self-Serve enrolment token, and only to `/enrolled`, `/simulations/{simId}`, and `/launch/play`.
 */
import axios from "axios";
import { API_ENDPOINT } from "../constants";
import { appendApiMessage } from "../utils/apiError";
import {
  PublicSsClass,
  SsCheckStudentRequest,
  SsCheckStudentResponse,
  SsEnrollRequest,
  SsForgotRequest,
  SsForgotResponse,
  SsEnrollResponse,
  SsEnrolledResponse,
  SsLaunchPlayResponse,
  SsLaunchRequest,
  SsPublicSimulationDetail,
  SsRegisterStudentRequest,
  SsStudentExistsResponse,
} from "../types/self-serve.public.types";

/** Base URL of the public Self-Serve class endpoints. */
const publicBase = () => `${API_ENDPOINT}public/ss-classes`;

/**
 * Copies the backend's stable `errorCode` (e.g. `SS_STUDENT_NOT_FOUND`) from the
 * response body onto the thrown error, so callers can branch on it without parsing messages.
 *
 * @param error axios error from a failed request
 * @returns the same error, with `errorCode` set when the body had one
 */
function attachErrorCode(error: any): any {
  const code = error?.response?.data?.errorCode;
  if (error && typeof code === "string") {
    error.errorCode = code;
  }
  return error;
}

/**
 * Returns `data.data` when the API wrapped the payload in a `{ data: ... }` envelope,
 * otherwise the body itself.
 */
function unwrapBody(data: any): any {
  return data?.data && typeof data.data === "object" ? data.data : data;
}

/**
 * Reads the enroll response into a typed session. Non-integer ids become `undefined`,
 * which the landing page treats as "could not start your session".
 */
function parseSession(body: any): SsEnrollResponse {
  const stdId = Number(body?.stdId);
  const classId = Number(body?.classId);
  return {
    enrolmentToken: body?.enrolmentToken || "",
    stdId: Number.isInteger(stdId) ? stdId : undefined,
    classId: Number.isInteger(classId) ? classId : undefined,
  };
}

/**
 * Reads one page of the enrolled response defensively: a missing list becomes `[]` and
 * non-integer paging fields become `undefined`.
 */
function unwrapEnrolled(data: any): SsEnrolledResponse {
  const body = unwrapBody(data);
  const currentPage = Number(body?.currentPage);
  const totalPages = Number(body?.totalPages);
  return {
    className: typeof body?.className === "string" ? body.className : undefined,
    studentName: typeof body?.studentName === "string" ? body.studentName : undefined,
    simulations: Array.isArray(body?.simulations) ? body.simulations : [],
    currentPage: Number.isInteger(currentPage) ? currentPage : undefined,
    totalPages: Number.isInteger(totalPages) ? totalPages : undefined,
  };
}

/**
 * Loads the public landing data for a class (name, instructions, enabled tabs, price).
 * Errors are thrown as-is: 404 = unknown or deleted class, 410 = class inactive.
 *
 * @param slug landing slug from `/class/:slug`
 */
export async function resolvePublicSsClass(slug: string): Promise<PublicSsClass> {
  const res = await axios.get(
    `${publicBase()}/${encodeURIComponent(slug)}`
  );
  return res.data;
}

/**
 * Asks the public landing to resend a usable code for this email, if one exists.
 * Plain axios, same as {@link resolvePublicSsClass}: no portal `Authorization` header,
 * no cookies (`withCredentials: false`), and no enrolment token. A signed-in admin
 * session is not attached. HTTP 200 is `{ acknowledged: true }` for a match and a miss.
 * Errors are thrown as-is; the page reads only `response.status`, never the API message.
 *
 * @param slug landing slug from `/class/:slug`
 * @param body trimmed email only
 */
export async function forgot(
  slug: string,
  body: SsForgotRequest
): Promise<SsForgotResponse> {
  const res = await axios.post(
    `${publicBase()}/${encodeURIComponent(slug)}/forgot`,
    { email: body.email },
    { withCredentials: false }
  );
  return { acknowledged: res.data?.acknowledged === true };
}

/** HTTP status of a failed request from this service, or undefined when there was no response. */
export const httpStatusOf = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

/**
 * Whether this email already belongs to the class, and whether that student has paid.
 * `res.data` is `{ studentExists, paymentDone }`.
 *
 * @param slug landing slug from `/class/:slug`
 * @param email trimmed student email
 */
export const studentExists = async (
  slug: string,
  email: string
): Promise<SsStudentExistsResponse> => {
  const res = await axios.post(
    `${publicBase()}/${encodeURIComponent(slug)}/exists`,
    { email, slug }
  );
  const body = res.data;
  if (!body || typeof body !== "object") {
    throw new Error("Unable to verify this email. Please try again.");
  }
  return {
    studentExists: body.studentExists === true,
    paymentDone: body.paymentDone === true,
  };
};

/**
 * Checks whether a SELF_SERVE student already exists for this email and access code.
 * A bookstore code with no student sets `registrationRequired`. Student Pay with no
 * matching student throws `SS_STUDENT_NOT_FOUND`.
 *
 * @param slug landing slug
 * @param body email and access code
 */
export async function checkStudent(
  slug: string,
  body: SsCheckStudentRequest
): Promise<SsCheckStudentResponse> {
  try {
    const res = await axios.post(
      `${publicBase()}/${encodeURIComponent(slug)}/check-student`,
      body
    );
    const payload = unwrapBody(res.data);
    return {
      studentExists: payload?.studentExists === true,
      registrationRequired: payload?.registrationRequired === true,
      vendorKey: typeof payload?.vendorKey === "string" ? payload.vendorKey : undefined,
    };
  } catch (error) {
    await appendApiMessage(error, "We could not check that access code.");
    throw attachErrorCode(error);
  }
}

/**
 * Registers a SELF_SERVE student for a bookstore access code. Does not enrol them.
 *
 * @param slug landing slug
 * @param body email, code, and student name
 */
export async function registerStudent(
  slug: string,
  body: SsRegisterStudentRequest
): Promise<void> {
  try {
    await axios.post(
      `${publicBase()}/${encodeURIComponent(slug)}/register-student`,
      body
    );
  } catch (error) {
    await appendApiMessage(error, "We could not register you for this class.");
    throw attachErrorCode(error);
  }
}

/**
 * Enrols an existing student with an access code. On success returns the enrolment token
 * used by `/enrolled`.
 *
 * On failure the thrown error carries `userMessage` (text to show) and `errorCode`.
 * Codes the landing page acts on:
 * - `SS_REDEEM_CONFIRM_REVOKE`: student already has access, open the replace-code dialog
 * - `SS_STUDENT_NOT_FOUND`: no student for this email; ask them to check the email and code
 * - `SS_REDEEM_CONFLICT`: another enrolment is in progress, ask to retry
 * - 404 / 410 status: the class is gone or no longer accepting registrations
 *
 * @param slug landing slug
 * @param body email, code, optional confirmRevoke
 */
export async function enrollStudent(
  slug: string,
  body: SsEnrollRequest
): Promise<SsEnrollResponse> {
  try {
    const res = await axios.post(
      `${publicBase()}/${encodeURIComponent(slug)}/enroll`,
      body
    );
    return parseSession(unwrapBody(res.data));
  } catch (error) {
    await appendApiMessage(error, "We could not redeem that access code.");
    throw attachErrorCode(error);
  }
}

/** Simulations per page on Access Simulation (the backend allows at most 50). */
export const ENROLLED_PAGE_SIZE = 10;

/**
 * Loads one page of the student's assigned simulations. Sends the enrolment token as a
 * Bearer header; the backend re-checks that the student still has current access.
 * 401/403 means the token is expired, revoked, or for another class.
 *
 * @param slug landing slug; must match the class in the token
 * @param enrolmentToken token returned by {@link enrollStudent}
 * @param pageNumber 1-based page
 * @param pageSize page length
 */
export async function getEnrolled(
  slug: string,
  enrolmentToken: string,
  pageNumber: number = 1,
  pageSize: number = ENROLLED_PAGE_SIZE
): Promise<SsEnrolledResponse> {
  try {
    const res = await axios.get(
      `${publicBase()}/${encodeURIComponent(slug)}/enrolled`,
      {
        headers: { Authorization: `Bearer ${enrolmentToken}` },
        params: { page_number: pageNumber, page_size: pageSize },
      }
    );
    return unwrapEnrolled(res.data);
  } catch (error) {
    await appendApiMessage(error, "We could not load your simulations. Please try again.");
    throw attachErrorCode(error);
  }
}

/**
 * Mints the SimBuilder play path for an enrolled student when they press Play.
 * The server checks the enrolment and that the sim is active on the class, and creates the
 * student's link on the first play. 401/403 means the enrolment session is invalid.
 *
 * @param slug landing slug
 * @param enrolmentToken token from sessionStorage
 * @param body `{ simId }`
 */
export async function playSimulation(
  slug: string,
  enrolmentToken: string,
  body: SsLaunchRequest
): Promise<SsLaunchPlayResponse> {
  try {
    const res = await axios.post(
      `${publicBase()}/${encodeURIComponent(slug)}/launch/play`,
      body,
      { headers: { Authorization: `Bearer ${enrolmentToken}` } }
    );
    const payload = unwrapBody(res.data);
    const ssoPath = typeof payload?.ssoPath === "string" ? payload.ssoPath : "";
    return { ssoPath };
  } catch (error) {
    await appendApiMessage(error, "We could not start that simulation. Please try again.");
    throw attachErrorCode(error);
  }
}

/**
 * Loads simulation detail for the public Self-Serve detail page. The backend returns it only
 * when the sim is active on the enrolled class: 404 (`SS_SIMULATION_NOT_FOUND`) means the sim
 * is not on this class, 401/403 means the enrolment session is invalid.
 *
 * @param slug landing slug; must match the class in the token
 * @param enrolmentToken token from sessionStorage
 * @param simId simulation id from the route
 */
export async function getClassSimulation(
  slug: string,
  enrolmentToken: string,
  simId: number
): Promise<SsPublicSimulationDetail> {
  try {
    const res = await axios.get(
      `${publicBase()}/${encodeURIComponent(slug)}/simulations/${simId}`,
      { headers: { Authorization: `Bearer ${enrolmentToken}` } }
    );
    const body = unwrapBody(res.data);
    return mapPublicSimulation(body, simId);
  } catch (error) {
    await appendApiMessage(error, "We could not load that simulation. Please try again.");
    throw attachErrorCode(error);
  }
}

/**
 * Maps the simulation detail body (same shape as `GET /api/sims/{id}`) to the fields ViewSimPage needs.
 */
function mapPublicSimulation(body: any, fallbackSimId: number): SsPublicSimulationDetail {
  const simId = Number(body?.simId);
  return {
    simId: Number.isInteger(simId) ? simId : fallbackSimId,
    simName: typeof body?.simName === "string" ? body.simName : "",
    detailText: typeof body?.detailText === "string" ? body.detailText : "",
    simDuration: typeof body?.simDuration === "string" ? body.simDuration : "",
    thumbnail: typeof body?.thumbnail === "string" ? body.thumbnail : "",
    preview: typeof body?.preview === "string" ? body.preview : "",
    docFile1: body?.docFile1,
    docAccess1: body?.docAccess1,
    docName1: body?.docName1,
    docFile2: body?.docFile2,
    docAccess2: body?.docAccess2,
    docName2: body?.docName2,
    docFile3: body?.docFile3,
    docAccess3: body?.docAccess3,
    docName3: body?.docName3,
    docFile4: body?.docFile4,
    docAccess4: body?.docAccess4,
    docName4: body?.docName4,
    docFile5: body?.docFile5,
    docAccess5: body?.docAccess5,
    docName5: body?.docName5,
    simFacilitatorDocumentsListBySimId: Array.isArray(
      body?.simFacilitatorDocumentsListBySimId
    )
      ? body.simFacilitatorDocumentsListBySimId
      : [],
  };
}
